import { startAuthentication, startRegistration } from '@simplewebauthn/browser'
import { http } from './client'
import type {
  AddCredentialResponse,
  CeremonyBeginResponse,
  CredentialResponse,
  RegisterFinishResponse,
  TokenPairResponse,
} from './types'

// Опции от бэкенда приходят как JSON произвольной формы — библиотека ждёт свои типы.
// Приведение локализовано здесь, чтобы не расползалось по страницам.
type RegistrationOptions = Parameters<typeof startRegistration>[0]['optionsJSON']
type AuthenticationOptions = Parameters<typeof startAuthentication>[0]['optionsJSON']

const asRegistrationOptions = (options: Record<string, unknown>): RegistrationOptions =>
  options as unknown as RegistrationOptions

const asAuthenticationOptions = (options: Record<string, unknown>): AuthenticationOptions =>
  options as unknown as AuthenticationOptions

// === Вход ===

export async function loginWithPasskey(): Promise<TokenPairResponse> {
  const begin = await http.post<CeremonyBeginResponse>('/v0/auth/login/begin', undefined, {
    skipAuth: true,
  })
  const credential = await startAuthentication({
    optionsJSON: asAuthenticationOptions(begin.options),
  })
  return http.post<TokenPairResponse>(
    '/v0/auth/login/finish',
    { challengeId: begin.challengeId, credential },
    { skipAuth: true },
  )
}

// === Регистрация по приглашению ===

export async function registerWithInvite(inviteToken: string): Promise<RegisterFinishResponse> {
  const begin = await http.post<CeremonyBeginResponse>(
    '/v0/auth/register/begin',
    { inviteToken },
    { skipAuth: true },
  )
  const credential = await startRegistration({
    optionsJSON: asRegistrationOptions(begin.options),
  })
  return http.post<RegisterFinishResponse>(
    '/v0/auth/register/finish',
    { challengeId: begin.challengeId, credential },
    { skipAuth: true },
  )
}

// === Восстановление доступа ===

export async function recoverWithCode(userId: string, code: string): Promise<TokenPairResponse> {
  const begin = await http.post<CeremonyBeginResponse>(
    '/v0/auth/recovery/begin',
    { userId, code },
    { skipAuth: true },
  )
  const credential = await startRegistration({
    optionsJSON: asRegistrationOptions(begin.options),
  })
  return http.post<TokenPairResponse>(
    '/v0/auth/recovery/finish',
    { challengeId: begin.challengeId, credential },
    { skipAuth: true },
  )
}

// === Свои ключи ===

export const listCredentials = (): Promise<CredentialResponse[]> =>
  http.get<CredentialResponse[]>('/v0/auth/credentials')

export async function addCredential(name: string): Promise<AddCredentialResponse> {
  const begin = await http.post<CeremonyBeginResponse>('/v0/auth/credentials/begin')
  const credential = await startRegistration({
    optionsJSON: asRegistrationOptions(begin.options),
  })
  return http.post<AddCredentialResponse>('/v0/auth/credentials/finish', {
    challengeId: begin.challengeId,
    credential,
    name,
  })
}

export const deleteCredential = (credentialId: string): Promise<void> =>
  http.del<void>(`/v0/auth/credentials/${credentialId}`)

// === Админские: ключи пользователя ===

export const listUserCredentials = (userId: string): Promise<CredentialResponse[]> =>
  http.get<CredentialResponse[]>(`/v0/users/${userId}/credentials`)

export const deleteUserCredential = (userId: string, credentialId: string): Promise<void> =>
  http.del<void>(`/v0/users/${userId}/credentials/${credentialId}`)
